"use client";
import { getGlobalMessage, getGlobalNotification } from '@/components/common/GlobalAppProvider';
import { AppConfig } from '@/config/app';
import { useUserStore } from '@/context/user-context';
import logger from '@/lib/edge-logger';
import { showGlobalError } from '@/lib/global-modal';
import { LoginToken } from '@/types/frontend';
import { message } from 'antd';

const API_ROOT = process.env.NEXT_PUBLIC_API_ROOT || '';

// 用于跟踪是否已经弹出了网络连接失败的提示
let networkErrorShown = false;
// 提示显示的时间戳
let networkErrorTimestamp = 0;
// 冷却时间（毫秒）- 5秒
const NETWORK_ERROR_COOLDOWN = 5000;

/**
 * 判断是否为可重试的瞬时网络错误（连接被重置/无响应），业务错误与用户主动取消不重试
 */
export const isRetryableNetworkError = (error: any): boolean => {
    if (error?.name === 'AbortError') return false;
    return error instanceof TypeError && String(error?.message || '').includes('Failed to fetch');
};

/**
 * 对网络请求包装自动重试（仅针对瞬时网络错误，指数退避）
 * 适用于批量任务中的单个分片请求，避免一次连接重置中断整个任务
 */
export const withNetworkRetry = async <T>(
    fn: () => Promise<T>,
    retries: number = 3,
    baseDelayMs: number = 1000
): Promise<T> => {
    let lastError: any;
    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            return await fn();
        } catch (error: any) {
            lastError = error;
            if (!isRetryableNetworkError(error) || attempt === retries) {
                throw error;
            }
            await new Promise((resolve) => setTimeout(resolve, baseDelayMs * (attempt + 1)));
        }
    }
    throw lastError;
};

/**
 * 限制并发度地执行一批异步任务（任务间相互独立，任一失败则整体 reject）
 */
export const runWithConcurrency = async <T>(
    limit: number,
    tasks: Array<() => Promise<T>>
): Promise<T[]> => {
    const results: T[] = new Array(tasks.length);
    let nextIndex = 0;
    const workers = Array.from({ length: Math.max(1, Math.min(limit, tasks.length)) }, async () => {
        while (nextIndex < tasks.length) {
            const index = nextIndex++;
            const task = tasks[index];
            if (!task) continue;
            results[index] = await task();
        }
    });
    await Promise.all(workers);
    return results;
};

export enum Method {
    GET = 'GET',
    POST = 'POST',
    DELETE = "DELETE",
    PUT = "PUT"
}

async function doFetch(url: string, method: Method, body: unknown, token: LoginToken | null, customHeaders?: Record<string, string>, signal?: AbortSignal) {
    // 检查是否是 FormData
    const isFormData = body instanceof FormData;

    return await fetch(API_ROOT + url, {
        method,
        // 允许POST、PUT和DELETE方法带有body
        ...([Method.POST, Method.PUT, Method.DELETE].includes(method) && body !== undefined && body !== null ? { body: isFormData ? body : JSON.stringify(body) } : {}),
        headers: {
            // 如果是 FormData，不设置 Content-Type，让浏览器自动设置
            ...(!isFormData && { 'Content-Type': 'application/json' }),
            ...(token ? { [AppConfig.headers.authToken]: token.token } : {}),
            ...customHeaders
        },
        ...(signal ? { signal } : {})
    });
}

export interface RequestOptions {
    loadingMessage?: string;
    successMessage?: string;
    headers?: Record<string, string>;
    messageInstance?: any; // 传入的 message 实例，用于避免静态方法警告
    useNotification?: boolean; // 失败时使用全局 notification 常驻展示错误原因，适合需要让用户看清失败原因的场景
    signal?: AbortSignal; // 支持调用方取消请求（AbortController），中断底层 fetch
    suppressNetworkErrorTip?: boolean; // 抑制"网络连接失败"全局弹窗（调用方自行重试时避免打断用户）
}

export const get = async (url: string, params?: Record<string, unknown>, options?: RequestOptions) => {
    const queryString = buildQueryString(params);
    return await request(`${url}${queryString}`, null, options?.loadingMessage, options?.successMessage, Method.GET, options?.headers, options?.messageInstance, options?.useNotification, options?.signal, options?.suppressNetworkErrorTip);
}
export const post = async (url: string, body: unknown, params?: Record<string, unknown>, options?: RequestOptions) => {
    const queryString = buildQueryString(params);
    return await request(`${url}${queryString}`, body, options?.loadingMessage, options?.successMessage, Method.POST, options?.headers, options?.messageInstance, options?.useNotification, options?.signal, options?.suppressNetworkErrorTip);
}

export const put = async (url: string, body: unknown, params?: Record<string, unknown>, options?: RequestOptions) => {
    const queryString = buildQueryString(params);
    return await request(`${url}${queryString}`, body, options?.loadingMessage, options?.successMessage, Method.PUT, options?.headers, options?.messageInstance, options?.useNotification, options?.signal, options?.suppressNetworkErrorTip);
}

export const del = async (url: string, params?: Record<string, unknown>, options?: RequestOptions, body?: unknown) => {
    const queryString = buildQueryString(params);
    return await request(`${url}${queryString}`, body || {}, options?.loadingMessage, options?.successMessage, Method.DELETE, options?.headers, options?.messageInstance, options?.useNotification, options?.signal, options?.suppressNetworkErrorTip);
}

export const request = async (url: string, body: unknown, loadingMessage?: string, successMessage?: string, method: Method = Method.POST, headers: Record<string, string> = {}, externalMessage?: any, useNotification?: boolean, signal?: AbortSignal, suppressNetworkErrorTip?: boolean) => {
    const useApiTip = headers[AppConfig.headers.useApiTip]
    try {
        // 优先使用传入的 message 实例，其次使用全局 message，最后使用静态 message
        const msg = externalMessage || getGlobalMessage();
        
        if (loadingMessage) {
            msg.destroy?.();
            // 延迟到 effect 阶段执行，避免 React 18 concurrent mode 警告
            setTimeout(() => {
                // 检查是否是静态 message 对象（通过检查是否有 _internalApi 等属性）
                if (msg !== message || (msg as any)._internalApi) {
                    msg.loading?.(loadingMessage);
                }
            }, 0);
        }

        const { token, refreshToken, user } = useUserStore.getState();

        // 统一为 cargo-master 代理请求注入 operator（id:name），后端据此记录真实的 created_by/updated_by，避免 unknown
        if (user && url.includes('/api/proxy/cargo-master/') && !/[?&]operator=/.test(url)) {
            url += (url.includes('?') ? '&' : '?') + 'operator=' + encodeURIComponent(`${user.id}:${user.name}`);
        }

        // 第一次请求
        let response = await doFetch(url, method, body, token, headers, signal);

        // 如果token过期，尝试刷新
        if (response.status === 401) {
            if (token?.refreshToken) {
                const refreshResponse = await fetchNewToken(token);

                if (refreshResponse.ok) {
                    const newTokenData = await refreshResponse.json();
                    refreshToken(newTokenData);
                    response = await doFetch(url, method, body, newTokenData, headers, signal);
                } else {
                    logger.info('Token刷新失败，需要重新登录');
                    const { logout } = useUserStore.getState();
                    if (logout) {
                        logout();
                    }
                    window.location.href = '/login';
                    throw new Error('Token过期，已跳转到登录页面');
                }
            } else {
                logger.info('没有refreshToken，需要重新登录');
                const { logout } = useUserStore.getState();
                if (logout) {
                    logout();
                }
                window.location.href = '/login';
                throw new Error('Token过期且无refreshToken，已跳转到登录页面');
            }
        }

        let data: { code: any; desc?: any; data: any; };
        try {
            const text = await response.text();
            if (!text) {
                data = { code: '0', data: {} };
            } else {
                data = JSON.parse(text);
            }
        } catch (error: any) {
            logger.error('JSON 解析错误:', error);
            data = { code: '0', data: {} };
        }

        logger.debug(`[${method}] ${url}`, {
            details: { request: { headers, body }, response: { ok: response.ok, status: response.status, data } },
            metadata: { key: `api-util.ts|${method}|${url.split('?')[0]}` }
        });

        if (!response.ok || data?.code !== '0') {
            const errorMessage = data?.desc || response.statusText;
            // 仅拼接字符串类型的详情，避免对象被隐式转成 "[object Object]"
            const errorDetail = typeof data?.data === 'string' ? data.data : '';
            const fullErrorMessage = errorDetail ? `${errorMessage}: ${errorDetail}` : errorMessage;
            if (useApiTip &&useApiTip !== 'false') {
                msg.destroy?.();
                // 延迟到 effect 阶段执行，避免 React 18 concurrent mode 警告
                setTimeout(() => {
                    // 检查是否是静态 message 对象（通过检查是否有 _internalApi 等属性）
                    if (msg !== message || (msg as any)._internalApi) {
                        msg.error?.(fullErrorMessage || '请求失败');
                    }
                }, 0);
            }
            if (useNotification) {
                // 使用全局 notification 常驻展示，message 为主标题、description 为后端返回的真实原因
                const notify = getGlobalNotification();
                const description = errorDetail
                    ? (typeof errorDetail === 'string' ? errorDetail : JSON.stringify(errorDetail))
                    : undefined;
                setTimeout(() => {
                    notify.error?.({
                        message: errorMessage || '请求失败',
                        description,
                        duration: 0,
                    });
                }, 0);
            }
            const err: any = new Error(fullErrorMessage);
            err.name = data?.code;
            // 附带结构化字段，供调用方弹错误详情弹窗（ErrorModal）时取用
            err.code = data?.code;
            err.desc = errorMessage;
            err.detail = errorDetail || undefined;
            throw err;
            // return { code: data?.code || '0', desc: fullErrorMessage || '请求失败', data: null };
        }

        if (successMessage) {
            msg.destroy?.();
            // 延迟到 effect 阶段执行，避免 React 18 concurrent mode 警告
            setTimeout(() => {
                // 检查是否是静态 message 对象（通过检查是否有 _internalApi 等属性）
                if (msg !== message || (msg as any)._internalApi) {
                    msg.success?.(successMessage, 1);
                }
            }, 0);
        } else if (useApiTip !== 'false') {
            msg.destroy?.();
            // 延迟到 effect 阶段执行，避免 React 18 concurrent mode 警告
            setTimeout(() => {
                // 检查是否是静态 message 对象（通过检查是否有 _internalApi 等属性）
                if (msg !== message || (msg as any)._internalApi) {
                    msg.success?.(data.desc, 1);
                }
            }, 0);
        }

        return data;
    } catch (error: any) {
        // 用户主动取消（AbortController）：不弹任何错误提示，直接抛回让调用方判断
        const isAborted = signal?.aborted || error?.name === 'AbortError' || error?.name === 'DOMException' && error?.message?.includes('abort');
        if (isAborted) {
            throw error;
        }
        // 错误处理使用外部传入的 msg 或全局 message
        const errorMsg = externalMessage || getGlobalMessage();
        if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
            // 调用方自行重试时抑制全局弹窗，避免重试成功仍打断用户
            if (!suppressNetworkErrorTip) {
                const now = Date.now();
                // 检查是否已经在冷却时间内显示过提示
                if (!networkErrorShown || (now - networkErrorTimestamp) > NETWORK_ERROR_COOLDOWN) {
                    networkErrorShown = true;
                    networkErrorTimestamp = now;
                    // 使用全局 Modal 显示错误
                    showGlobalError({
                        title: '网络连接失败',
                        content: '网络连接失败或服务器无法访问，请检查网络后重试或联系管理员',
                        okText: '确定',
                        onOk: () => {
                            // 模态框关闭时，重置显示状态
                            networkErrorShown = false;
                        }
                    });
                }
            }
        } else if (useApiTip !== 'false' && !useNotification) {
            errorMsg.destroy?.();
            // 延迟到 effect 阶段执行，避免 React 18 concurrent mode 警告
            setTimeout(() => {
                // 检查是否是静态 message 对象（通过检查是否有 _internalApi 等属性）
                if (errorMsg !== message || (errorMsg as any)._internalApi) {
                    errorMsg.error?.(`${error.message ?? '请求失败:未知错误'}`);
                }
            }, 0);
        }
        throw error;
    }
};

function buildQueryString(params: Record<string, unknown> | undefined) {
    return params && Object.keys(params).length > 0 ?
        `?${new URLSearchParams(Object.entries(params).reduce((acc, [key, val]) => {
            if (val !== undefined && val !== null) {
                acc[key] = String(val);
            }
            return acc;
        }, {} as Record<string, string>)).toString()}` :
        '';
}

async function fetchNewToken(token: LoginToken) {
    return await fetch(API_ROOT + '/api/v1/auth/refresh', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            [AppConfig.headers.refreshToken]: token.refreshToken || ''
        }
    });
}

